import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  Search,
  Settings,
  LogOut,
  User,
  Car,
  Copy,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { tenantApi } from '@/api'
import { getNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead, type Notification } from '@/api/notifications'

const NOTIFICATION_POLL_MS = 20000

export default function Header() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const isTenantAdmin = user?.role === 'tenant_admin'
  const [linkCopying, setLinkCopying] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [notifOpen, setNotifOpen] = useState(false)

  const refreshUnreadCount = useCallback(() => {
    getUnreadNotificationCount().then(setUnreadCount).catch(() => {})
  }, [])

  useEffect(() => {
    refreshUnreadCount()
    const timer = setInterval(refreshUnreadCount, NOTIFICATION_POLL_MS)
    return () => clearInterval(timer)
  }, [refreshUnreadCount])

  const handleNotifOpenChange = (open: boolean) => {
    setNotifOpen(open)
    if (open) {
      getNotifications().then(res => setNotifications(res.data)).catch(() => {})
    }
  }

  const handleNotificationClick = async (n: Notification) => {
    if (!n.is_virtual && !n.is_read) {
      try { await markNotificationRead(n.id) } catch { /* ignore */ }
    }
    refreshUnreadCount()
    setNotifOpen(false)
    if (n.order_id) navigate(`/orders?editOrder=${n.order_id}`)
    else if (n.driver_id) navigate('/drivers')
  }

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead()
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      refreshUnreadCount()
    } catch { /* ignore */ }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleCopyBookingLink = async () => {
    const tenantId = JSON.parse(localStorage.getItem('tenant') || '{}')?.id
    if (!tenantId) return
    setLinkCopying(true)
    try {
      const token = await tenantApi.getBookingLink(tenantId)
      const url = `${window.location.origin}/booking/tenant/${token}`
      await navigator.clipboard.writeText(url)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (error) {
      alert('取得訂單連結失敗，請稍後再試')
    } finally {
      setLinkCopying(false)
    }
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800 shadow-sm">
      {/* Mobile Logo (Visible only on mobile) */}
      <div className="flex items-center gap-2 lg:hidden mr-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
          <Car className="h-5 w-5 text-white" />
        </div>
      </div>

      {/* Spacer to push content to the right */}
      <div className="flex-1" />

      {/* Right Side Actions */}
      <div className="flex items-center gap-2">
        {/* 複製訂單連結 */}
        {isTenantAdmin && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyBookingLink}
            disabled={linkCopying}
            title="複製公用訂單連結，可直接貼給客戶下單"
            className="gap-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <Copy className="h-4 w-4" />
            <span className="hidden sm:inline">{linkCopied ? '已複製連結！' : '複製訂單連結'}</span>
          </Button>
        )}

        {/* Notifications */}
        <DropdownMenu open={notifOpen} onOpenChange={handleNotifOpenChange}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative text-gray-500 hover:text-gray-700 dark:text-gray-400">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white border-2 border-white dark:border-gray-800">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-80" align="end" forceMount>
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="p-0 font-normal text-sm">通知</DropdownMenuLabel>
              {notifications.some(n => !n.is_read && !n.is_virtual) && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                >
                  全部標為已讀
                </button>
              )}
            </div>
            <DropdownMenuSeparator />
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-gray-400">目前沒有通知</div>
              ) : (
                notifications.map(n => (
                  <DropdownMenuItem
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`flex flex-col items-start gap-0.5 whitespace-normal py-2 cursor-pointer ${!n.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  >
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{n.title}</span>
                    {n.message && <span className="text-xs text-gray-500 dark:text-gray-400">{n.message}</span>}
                    <span className="text-[10px] text-gray-400">{new Date(n.created_at).toLocaleString('zh-TW')}</span>
                  </DropdownMenuItem>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu Dropdown (Replaces Sidebar Bottom User Section on Mobile, Unifies on Desktop) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0 overflow-hidden border border-gray-200 dark:border-gray-700 ml-1">
              <div className="flex h-full w-full items-center justify-center bg-blue-100 dark:bg-blue-900">
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.name}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.role}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              <span>系統設定</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400">
              <LogOut className="mr-2 h-4 w-4" />
              <span>登出系統</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
